import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import group1 from "../../assets/Group 1.svg";
import User1 from "../../assets/User1.svg";
import notification from "../../assets/notification-bell1394.svg";
import vectorC from "../../assets/Vector copy.svg";
import DAW from "../../assets/VectorDAW.svg";
import DAB from "../../assets/VectorDAB.svg";
import contentB from "../../assets/Group 1ContentB.svg";
import notifiB from "../../assets/NBB.svg";
import appManager from "../../assets/Icon/setting.svg";

const APPS = {
  LXDC: {
    label: "LXDC",
    links: [
      { to: "/", label: "Data Analysis", iconOff: DAW, iconOn: DAB },
      { to: "/user", label: "User", iconOff: vectorC, iconOn: User1 },
      { to: "/content", label: "Content", iconOff: group1, iconOn: contentB },
      {
        to: "/notifications",
        label: "Notification",
        iconOff: notification,
        iconOn: notifiB,
      },
      {
        to: "/appmanager",
        label: "App Manager",
        iconOff: appManager,
        iconOn: appManager,
      },
      { to: "/email", label: "Email", iconOff: appManager, iconOn: appManager },
    ],
  },
  Vortex: {
    label: "Vortex",
    links: [
      { to: "/vortexData", label: "Data Analysis", iconOff: DAW, iconOn: DAB },
      { to: "/vortexusers", label: "User", iconOff: vectorC, iconOn: User1 },
      {
        to: "/vortexnotifications",
        label: "Notification",
        iconOff: notification,
        iconOn: notifiB,
      },
      {
        to: "/vortexappmanager",
        label: "App Manager",
        iconOff: appManager,
        iconOn: appManager,
      },
      {
        to: "/vortexemail",
        label: "Email",
        iconOff: appManager,
        iconOn: appManager,
      },
    ],
  },
};

const COLLAPSED_W = "3.5rem";
const EXPANDED_W = "17.875rem";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(true);
  const [activeApp, setActiveApp] = useState("LXDC");
  const [activeLink, setActiveLink] = useState(location.pathname);
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleAppSwitch = (appKey) => {
    setActiveApp(appKey);
    setDropOpen(false);
    const firstLink = APPS[appKey].links[0].to;
    navigate(firstLink);
    setActiveLink(firstLink);
  };

  const currentLinks = APPS[activeApp].links;

  return (
    <div
      className="mq900:hidden fixed top-[8rem] left-0 z-20 bg-gray-200 rounded-r-xl overflow-hidden transition-[width] duration-300 ease-in-out"
      style={{ width: open ? EXPANDED_W : COLLAPSED_W }}
    >
      {/* Inner wrapper always full width — outer clips it */}
      <div className="flex flex-col pb-16" style={{ width: EXPANDED_W }}>
        {/* ── Row 1: Hamburger toggle — always in the icon rail ── */}
        <div className="flex items-center h-14 px-[0.875rem] flex-shrink-0">
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
            className="flex flex-col justify-center items-center gap-[5px] w-6 h-6 flex-shrink-0 bg-transparent border-none cursor-pointer p-0 group"
          >
            <span className="block w-5 h-[2px] bg-white rounded-full transition-all duration-300 group-hover:bg-gray-600" />
            <span className="block w-5 h-[2px] bg-white rounded-full transition-all duration-300 group-hover:bg-gray-600" />
            <span className="block w-5 h-[2px] bg-white rounded-full transition-all duration-300 group-hover:bg-gray-600" />
          </button>
        </div>

        {/* ── Row 2: App switcher — visible only when expanded ── */}
        <div
          className={`px-[0.875rem] mb-4 transition-opacity duration-200 ${
            open
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div ref={dropRef} className="relative">
            <button
              onClick={() => setDropOpen((v) => !v)}
              className="w-full px-3 py-1.5 bg-white border-none rounded-lg flex items-center justify-between font-medium text-sm cursor-pointer hover:bg-gray-100 transition-colors duration-150"
            >
              <span>{APPS[activeApp].label}</span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                className={`transition-transform duration-200 ${dropOpen ? "rotate-180" : "rotate-0"}`}
              >
                <polyline
                  points="1,3 5,7 9,3"
                  fill="none"
                  stroke="#000"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {dropOpen && (
              <div className="absolute top-[calc(100%+4px)] left-0 w-full z-40 bg-white rounded-lg shadow-md overflow-hidden">
                {Object.entries(APPS).map(([key, app]) => (
                  <button
                    key={key}
                    onClick={() => handleAppSwitch(key)}
                    className={`
                      w-full px-3 py-2 text-left text-sm border-none cursor-pointer
                      transition-colors duration-100
                      ${
                        key === activeApp
                          ? "bg-gray-450 font-semibold"
                          : "bg-transparent font-normal hover:bg-gray-50"
                      }
                    `}
                  >
                    {app.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Row 3: Nav links ── */}
        <nav className="flex flex-col gap-1 px-2">
          {currentLinks.map(({ to, label, iconOff, iconOn }) => {
            const isActive = activeLink === to;
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setActiveLink(to)}
                className={`
                  no-underline flex items-center gap-[1.188rem]
                  px-[0.688rem] py-3 rounded-md whitespace-nowrap
                  text-sm transition-colors duration-150
                  ${
                    isActive
                      ? "bg-white text-black font-medium"
                      : "bg-transparent text-white font-normal hover:bg-white/20"
                  }
                `}
              >
                <img
                  className="w-6 h-[1.313rem] flex-shrink-0"
                  loading="lazy"
                  alt={label}
                  src={isActive ? iconOn : iconOff}
                />
                <span
                  className={`transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`}
                >
                  {label}
                </span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
